import React from 'react';

import { useMountedState } from '@gogame/hooks-ts';
import {
  SET_TABLE_SETTINGS,
  useScreenDispatch,
  useScreenState,
} from '@gogame/screens';

import {
  useConceptFamiliesGrid,
  useIsUpdating,
} from 'scenes/Gallery/GalleryPage/ConceptFamilies/data';
import {
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
  SCOPE_CONCEPT_FAMILIES,
  VIEW_MODE_DATATABLE,
} from 'scenes/Gallery/GalleryPage/constants';

const ConceptFamiliesDataTableContext = React.createContext({
  data: null,
  isLoading: false,
  viewMode: VIEW_MODE_DATATABLE,
  columns: [],
  onReorder: () => {},
  onCollapse: () => {},
  onChangeSettings: () => {},
});

export const useConceptFamiliesCount = () =>
  React.useContext(ConceptFamiliesDataTableContext)?.data?.count || 0;

export const ConceptFamiliesDataTableProvider = ({ children }) => {
  const dispatch = useScreenDispatch();
  const [viewMode, setViewMode] = useMountedState(VIEW_MODE_DATATABLE);
  const screenState = useScreenState();

  const {
    [SCOPE_CONCEPT_FAMILIES]: { columns, tableSettings },
  } = screenState;

  const { data, isLoading, isRefetching } = useConceptFamiliesGrid({
    tableSettings: tableSettings,
    filters: screenState.filters,
    searchQuery: screenState.searchQuery,
    searchType: screenState.searchType,
  });

  const isUpdating = useIsUpdating();

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      scope: SCOPE_CONCEPT_FAMILIES,
      payload: settings,
    });
  };

  const handleReorder = (columns) => {
    dispatch({
      type: REORDER_COLUMNS,
      scope: SCOPE_CONCEPT_FAMILIES,
      payload: columns,
    });
  };

  const handleCollapse = (columns) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      scope: SCOPE_CONCEPT_FAMILIES,
      payload: columns,
    });
  };

  return (
    <ConceptFamiliesDataTableContext.Provider
      value={{
        data,
        isLoading: isLoading || isUpdating,
        isRefetching: isRefetching,
        tableSettings,
        columns,
        onChangeSettings: handleChangeSettings,
        onReorder: handleReorder,
        onCollapse: handleCollapse,
        viewMode,
        setViewMode,
        filters: screenState.filters,
        searchQuery: screenState.searchQuery,
      }}
    >
      {children}
    </ConceptFamiliesDataTableContext.Provider>
  );
};

export default ConceptFamiliesDataTableContext;
